import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calculator, ReceiptText, TrendingUp, TrendingDown, Wallet,
  AlertTriangle, Upload, Sparkles, Pencil, Trash2, Plus,
  Loader2, FileText, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "@/lib/api";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const MONTHS = [
  ["all", "Ganzes Jahr"],
  ["1", "Januar"], ["2", "Februar"], ["3", "März"],
  ["4", "April"], ["5", "Mai"], ["6", "Juni"],
  ["7", "Juli"], ["8", "August"], ["9", "September"],
  ["10", "Oktober"], ["11", "November"], ["12", "Dezember"],
];

const EMPTY = {
  supplier: "",
  document_date: "",
  payment_date: "",
  invoice_number: "",
  amount: "",
  vat_amount: "",
  original_amount: "",
  original_currency: "EUR",
  exchange_rate: "",
  exchange_rate_date: "",
  exchange_rate_source: "",
  category: "sonstiges",
  cost_type: "variabel",
  notes: "",
  items: [],
  splits: [],
  missing_info: false,
  tax_review: false,
};

const fmtDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : value;
};

function Heading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-payment-section-heading">
      <span className="stellar-payment-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

export default function Steuer() {
  const now = new Date();
  const qc = useQueryClient();

  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const params = useMemo(() => ({
    year: Number(year),
    ...(month !== "all" ? { month: Number(month) } : {}),
  }), [year, month]);

  const { data: overview = {} } = useQuery({
    queryKey: ["steuer-overview", params],
    queryFn: async () => (await api.get("/steuer/overview", { params })).data,
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ["steuer-expenses", params],
    queryFn: async () => (await api.get("/steuer/expenses", { params })).data,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["steuer-categories"],
    queryFn: async () => (await api.get("/steuer/categories")).data,
  });

  const {
    data: jahresabschluss = {},
    isLoading: jahresabschlussLoading,
    isError: jahresabschlussError,
  } = useQuery({
    queryKey: ["steuer-jahresabschluss", year],
    queryFn: async () => (
      await api.get("/steuer/jahresabschluss", {
        params: { year: Number(year) },
      })
    ).data,
  });

  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.label])),
    [categories]
  );

  const downloadJahresabschlussPdf = async () => {
    try {
      const response = await api.get("/steuer/jahresabschluss/pdf", {
        params: { year: Number(year) },
        responseType: "blob",
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Jahresabschluss-${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF wurde erstellt");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["steuer-overview"] });
    qc.invalidateQueries({ queryKey: ["steuer-expenses"] });
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      document_date: new Date().toISOString().slice(0, 10),
    });
    setFile(null);
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditing(expense);
    setForm({
      supplier: expense.supplier || "",
      document_date: expense.document_date || "",
      payment_date: expense.payment_date || "",
      invoice_number: expense.invoice_number || "",
      amount: expense.amount ?? "",
      vat_amount: expense.vat_amount ?? "",
      original_amount: expense.original_amount ?? expense.amount ?? "",
      original_currency: expense.original_currency || "EUR",
      exchange_rate: expense.exchange_rate ?? "",
      exchange_rate_date: expense.exchange_rate_date || "",
      exchange_rate_source: expense.exchange_rate_source || "",
      category: expense.category || "sonstiges",
      cost_type: expense.cost_type || "variabel",
      notes: expense.notes || "",
      items: expense.items || [],
      splits: expense.splits || [],
      missing_info: !!expense.missing_info,
      tax_review: !!expense.tax_review,
    });
    setFile(null);
    setDialogOpen(true);
  };

  const payload = () => ({
    ...form,
    amount: Number(form.amount || 0),
    vat_amount: form.vat_amount === "" ? null : Number(form.vat_amount),
    original_amount: form.original_amount === "" ? null : Number(form.original_amount),
    original_currency: form.original_currency || "EUR",
    exchange_rate: form.exchange_rate === "" ? null : Number(form.exchange_rate),
    exchange_rate_date: form.exchange_rate_date || "",
    exchange_rate_source: form.exchange_rate_source || "",
    items: (form.items || []).filter(Boolean),
    splits: form.splits || [],
  });

  const uploadDocument = async (expenseId, selectedFile) => {
    if (!selectedFile) return;
    const fd = new FormData();
    fd.append("file", selectedFile);
    await api.post(`/steuer/expenses/${expenseId}/document`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let expense;
      if (editing) {
        expense = (await api.put(`/steuer/expenses/${editing.id}`, payload())).data;
      } else {
        expense = (await api.post("/steuer/expenses", payload())).data;
      }
      if (file) await uploadDocument(expense.id, file);
      return expense;
    },
    onSuccess: () => {
      toast.success("Beleg gespeichert");
      setDialogOpen(false);
      setEditing(null);
      setFile(null);
      refresh();
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/steuer/expenses/${id}`),
    onSuccess: () => {
      toast.success("Beleg gelöscht");
      refresh();
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const closeDialog = async () => {
    if (editing?.draft) {
      try {
        await api.delete(`/steuer/expenses/${editing.id}`);
        refresh();
      } catch (err) {
        toast.error("Entwurf konnte nicht gelöscht werden.");
        return;
      }
    }

    setDialogOpen(false);
    setEditing(null);
    setFile(null);
    setForm(EMPTY);
  };

  const analyze = async () => {
    if (!file && !editing?.document) {
      toast.error("Bitte zuerst einen Beleg auswählen.");
      return;
    }

    setAiBusy(true);
    try {
      let expenseId = editing?.id;

      if (!expenseId) {
        const fd = new FormData();
        fd.append("file", file);

        const created = (
          await api.post("/steuer/upload-draft", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        ).data;

        expenseId = created.id;
        setEditing(created);
        setFile(null);
      } else if (file) {
        await uploadDocument(expenseId, file);
        setFile(null);
      }

      const result = (
        await api.post(
          `/steuer/expenses/${expenseId}/analyze`,
          null,
          {
            params: form.payment_date
              ? { payment_date: form.payment_date }
              : {},
          }
        )
      ).data;

      setForm((old) => ({
        ...old,
        supplier: result.supplier || "",
        document_date: result.document_date || "",
        invoice_number: result.invoice_number || "",
        amount: result.amount ?? "",
        vat_amount: result.vat_amount ?? "",
        original_amount: result.original_amount ?? result.amount ?? "",
        original_currency: result.original_currency || result.currency || "EUR",
        exchange_rate: result.exchange_rate ?? "",
        exchange_rate_date: result.exchange_rate_date || "",
        exchange_rate_source: result.exchange_rate_source || "",
        category: result.category || "sonstiges",
        cost_type: result.cost_type || "variabel",
        items: result.items || [],
        missing_info: !!result.missing_info,
        tax_review: !!result.tax_review,
        notes: result.confidence_note
          ? `${old.notes ? `${old.notes}\n` : ""}AI-Hinweis: ${result.confidence_note}`
          : old.notes,
      }));

      try {
        const duplicateCheck = (
          await api.get("/steuer/expenses/check-duplicate", {
            params: {
              supplier: result.supplier || "",
              invoice_number: result.invoice_number || "",
              document_date: result.document_date || "",
              amount: result.amount || 0,
              exclude_id: expenseId,
            },
          })
        ).data;

        if (duplicateCheck.duplicate && duplicateCheck.match) {
          const match = duplicateCheck.match;
          toast.warning(
            `Möglicher doppelter Beleg: ${match.supplier}` +
            `${match.invoice_number ? ` · Rechnung ${match.invoice_number}` : ""}` +
            ` · ${match.document_date} · ${Number(match.amount).toFixed(2)} €`,
            { duration: 10000 }
          );
        } else {
          toast.success("Beleg analysiert – bitte Angaben prüfen");
        }
      } catch {
        toast.success("Beleg analysiert – bitte Angaben prüfen");
      }

      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading stellar-heading-teal flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="stellar-module-icon stellar-module-teal">
            <Calculator />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">
              Steuer
            </h1>
            <div className="text-sm text-muted-foreground mt-1">
              Einnahmen, Ausgaben und Belege
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <TabsList>
          <TabsTrigger value="overview">Übersicht</TabsTrigger>
          <TabsTrigger value="expenses">Ausgaben & Belege</TabsTrigger>
          <TabsTrigger value="jahresabschluss">Jahresabschluss / ELSTER</TabsTrigger>
        </TabsList>

        <div className="premium-card p-4 flex flex-wrap gap-3 items-end">
          <div>
            <Label>Jahr</Label>
            <Input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-28 mt-1"
            />
          </div>
          {activeTab !== "jahresabschluss" && (
            <div className="min-w-[180px]">
              <Label>Zeitraum</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <TabsContent value="overview" className="space-y-5">
          <section className="stellar-payment-section">
            <Heading number="1" icon={Calculator} title="Ergebnis" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="premium-card stellar-summary-card p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex gap-1 items-center">
                  <TrendingUp className="h-4 w-4" /> Einnahmen
                </div>
                <div className="font-display text-2xl font-bold mt-2">
                  {fmtEur(overview.income || 0)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {overview.invoice_count || 0} ausgestellte Rechnungen
                </div>
              </div>

              <div className="premium-card stellar-summary-card p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex gap-1 items-center">
                  <TrendingDown className="h-4 w-4" /> Ausgaben
                </div>
                <div className="font-display text-2xl font-bold mt-2">
                  {fmtEur(overview.expenses || 0)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {overview.expense_count || 0} Belege
                </div>
              </div>

              <div className="premium-card stellar-summary-card stellar-summary-total p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex gap-1 items-center">
                  <Wallet className="h-4 w-4" /> Gewinn
                </div>
                <div className="font-display text-2xl font-bold mt-2">
                  {fmtEur(overview.profit || 0)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Einnahmen minus Ausgaben
                </div>
              </div>
            </div>
          </section>

          <section className="stellar-payment-section">
            <Heading number="2" icon={AlertTriangle} title="Prüfung" />

            <div className="grid gap-3">
              {(overview.unpaid_invoice_count || 0) > 0 ? (
                <div className="premium-card p-4 border-amber-200">
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4" />
                    {overview.unpaid_invoice_count} offene Rechnung(en) · {fmtEur(overview.unpaid_total || 0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 mb-3">
                    Die Rechnungsbeträge sind trotzdem vollständig in den Einnahmen enthalten.
                  </div>

                  <div className="space-y-2">
                    {(overview.unpaid_invoices || []).map((inv) => (
                      <Link
                        key={inv.id}
                        to={`/rechnungen/${inv.id}`}
                        className="flex justify-between gap-3 rounded-lg border p-3 text-sm hover:bg-muted/50"
                      >
                        <span>
                          <strong>{inv.invoice_number || "Rechnung"}</strong>
                          {inv.customer_name ? ` · ${inv.customer_name}` : ""}
                        </span>
                        <span className="font-semibold whitespace-nowrap">
                          offen {fmtEur(inv.balance)}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="premium-card p-4 flex gap-2 items-center text-sm">
                  <CheckCircle2 className="h-5 w-5" />
                  Keine offenen Rechnungen im gewählten Zeitraum.
                </div>
              )}

              {((overview.missing_info_count || 0) > 0 ||
                (overview.tax_review_count || 0) > 0) && (
                <div className="premium-card p-4">
                  <div className="font-semibold mb-2">Belege prüfen</div>
                  <div className="flex flex-wrap gap-2">
                    {(overview.missing_info_count || 0) > 0 && (
                      <Badge variant="outline">
                        {overview.missing_info_count} × Angabe fehlt
                      </Badge>
                    )}
                    {(overview.tax_review_count || 0) > 0 && (
                      <Badge variant="outline">
                        {overview.tax_review_count} × steuerlich klären
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="stellar-payment-section">
            <Heading number="3" icon={ReceiptText} title="Ausgaben nach Kategorie" />
            <div className="premium-card p-4">
              {Object.keys(overview.expenses_by_category || {}).length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Noch keine Ausgaben im gewählten Zeitraum.
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(overview.expenses_by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-3 border-b last:border-0 py-2 text-sm">
                        <span>{categoryMap[key] || key}</span>
                        <strong>{fmtEur(value)}</strong>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="jahresabschluss" className="space-y-5">
          {jahresabschlussLoading ? (
            <div className="premium-card p-6 flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Jahresabschluss wird geladen...
            </div>
          ) : jahresabschlussError ? (
            <div className="premium-card p-4 border-red-200 text-sm">
              Jahresabschluss konnte nicht geladen werden.
            </div>
          ) : (
            <div id="jahresabschluss-content">
              <section className="stellar-payment-section">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Heading number="1" icon={Calculator} title={`Jahresabschluss ${year}`} />
                  <Button onClick={downloadJahresabschlussPdf} className="rounded-xl">
                    <FileText className="h-4 w-4 mr-2" />
                    Jahresabschluss als PDF herunterladen
                  </Button>
                </div>

                <div className="premium-card p-4 mb-3 flex items-center gap-2 text-sm font-semibold">
                  {jahresabschluss.elster_help?.ready ? (
                    <>
                      <CheckCircle2 className="h-5 w-5" />
                      Bereit für ELSTER
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5" />
                      Bitte noch prüfen
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="premium-card stellar-summary-card p-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Einnahmen
                    </div>
                    <div className="font-display text-2xl font-bold mt-2">
                      {fmtEur(jahresabschluss.elster_help?.betriebseinnahmen?.amount || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Zahlungseingänge aus Rechnungen
                    </div>
                  </div>

                  <div className="premium-card stellar-summary-card p-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Ausgaben
                    </div>
                    <div className="font-display text-2xl font-bold mt-2">
                      {fmtEur(jahresabschluss.elster_help?.betriebsausgaben?.amount || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Nach Belegdatum
                    </div>
                  </div>

                  <div className="premium-card stellar-summary-card stellar-summary-total p-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Gewinn
                    </div>
                    <div className="font-display text-2xl font-bold mt-2">
                      {fmtEur(jahresabschluss.elster_help?.gewinn?.amount || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Einnahmen minus Ausgaben
                    </div>
                  </div>
                </div>

                <div className="premium-card p-4 mt-3 text-sm">
                  <strong>So wurde gerechnet:</strong>{" "}
                  {jahresabschluss.elster_help?.basis || "—"}
                </div>
              </section>

              <section className="stellar-payment-section">
                <Heading number="2" icon={FileText} title="ELSTER-Ausfüllhilfe" />

                <div className="premium-card overflow-hidden">
                  <div className="p-4 border-b">
                    <div className="font-semibold">
                      Einnahmen für ELSTER
                    </div>

                    <div className="flex flex-wrap justify-between gap-2 mt-2 text-sm">
                      <span>
                        Anlage EÜR · Zeile{" "}
                        {jahresabschluss.elster_help?.betriebseinnahmen?.elster_line || "—"}
                      </span>
                      <strong>
                        {fmtEur(jahresabschluss.elster_help?.betriebseinnahmen?.amount || 0)}
                      </strong>
                    </div>

                    <div className="text-xs text-muted-foreground mt-2">
                      Referenz:{" "}
                      {jahresabschluss.elster_help?.betriebseinnahmen?.line_reference || "—"}
                      {" · "}
                      {jahresabschluss.elster_help?.betriebseinnahmen?.line_status || ""}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="font-semibold mb-3">Ausgaben</div>

                    {(jahresabschluss.elster_help?.betriebsausgaben?.elster_values || []).length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        Keine Ausgaben für dieses Jahr vorhanden.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(jahresabschluss.elster_help?.betriebsausgaben?.elster_values || []).map((item) => (
                          <div
                            key={item.label}
                            className="rounded-lg border p-3 text-sm"
                          >
                            <div className="flex justify-between gap-3">
                              <strong>{item.label}</strong>
                              <strong className="whitespace-nowrap">
                                {fmtEur(item.amount || 0)}
                              </strong>
                            </div>

                            <div className="text-xs text-muted-foreground mt-2">
                              Anlage EÜR {item.elster_year || year}
                              {" · "}
                              {item.elster_line != null
                                ? `Zeile ${item.elster_line}`
                                : "Zuordnung noch offen"}
                            </div>

                            <div className="text-sm mt-1">
                              Betrag für ELSTER:{" "}
                              <strong>{fmtEur(item.amount || 0)}</strong>
                            </div>

                            <div className="text-xs text-muted-foreground mt-1">
                              {item.line_status || item.elster_line_status || "Zuordnung bitte prüfen"}
                            </div>

                            {item.review_required && (
                              <Badge variant="outline" className="mt-2">
                                Prüfen
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="stellar-payment-section">
                <Heading number="3" icon={AlertTriangle} title="Das bitte prüfen" />

                {(jahresabschluss.elster_help?.review_items || []).length === 0 ? (
                  <div className="premium-card p-4 flex gap-2 items-center text-sm">
                    <CheckCircle2 className="h-5 w-5" />
                    Alles in Ordnung. Nichts zu prüfen.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(jahresabschluss.elster_help?.review_items || []).map((item, index) => (
                      <div key={`${item.type}-${index}`} className="premium-card p-4">
                        <div className="flex justify-between gap-3">
                          <strong>{item.label}</strong>
                          <strong className="whitespace-nowrap">{fmtEur(item.amount || 0)}</strong>
                        </div>
                        {item.items?.length > 0 && (
                          <div className="text-sm mt-2">
                            <span className="font-medium">Produkt:</span>{" "}
                            {item.items.join(" · ")}
                          </div>
                        )}

                        {item.stellar_category && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Kategorie: {categoryMap[item.stellar_category] || item.stellar_category}
                          </div>
                        )}

                        {(item.document_date || item.payment_date) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Datum: {fmtDate(item.document_date || item.payment_date)}
                          </div>
                        )}

                        <div className="text-sm text-muted-foreground mt-2">
                          <span className="font-medium">Grund:</span> {item.note}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </TabsContent>

        <TabsContent value="expenses" className="space-y-5">
          <section className="stellar-payment-section">
            <Heading number="1" icon={ReceiptText} title="Ausgaben & Belege" />

            <div className="premium-card overflow-hidden">
              <div className="p-4 border-b flex flex-wrap gap-3 justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {expenses.length} Beleg(e)
                </div>
                <Button onClick={openNew} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="h-4 w-4 mr-1" /> Neuer Beleg
                </Button>
              </div>

              {expenses.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <ReceiptText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <div>Noch keine Belege.</div>
                  <div className="text-xs mt-1">
                    PDF oder Foto hochladen und auf Wunsch automatisch analysieren lassen.
                  </div>
                </div>
              ) : (
                <>
                  <div className="sm:hidden divide-y">
                    {expenses.map((e) => (
                      <article key={e.id} className="p-4">
                        <div className="flex justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs text-muted-foreground">{fmtDate(e.document_date)}</div>
                            <div className="font-semibold truncate mt-1">{e.supplier || "Ohne Lieferant"}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {categoryMap[e.category] || e.category}
                            </div>
                          </div>
                          <div className="font-display font-bold text-lg whitespace-nowrap">
                            {fmtEur(e.amount || 0)}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          {e.document && <Badge variant="outline"><FileText className="h-3 w-3 mr-1" /> Beleg</Badge>}
                          {e.missing_info && <Badge variant="outline">Angabe fehlt</Badge>}
                          {e.tax_review && <Badge variant="outline">steuerlich klären</Badge>}
                        </div>

                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => openEdit(e)}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (window.confirm("Beleg wirklich löschen?")) deleteMutation.mutate(e.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="hidden sm:block overflow-x-auto">
                    <table className="stellar-data-table w-full min-w-[760px] text-sm">
                      <thead>
                        <tr>
                          <th className="text-left px-4 py-3">Datum</th>
                          <th className="text-left px-4 py-3">Lieferant</th>
                          <th className="text-left px-4 py-3">Kategorie</th>
                          <th className="text-left px-4 py-3">Typ</th>
                          <th className="text-right px-4 py-3">Betrag</th>
                          <th className="text-right px-4 py-3">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenses.map((e) => (
                          <tr key={e.id}>
                            <td className="px-4 py-3">{fmtDate(e.document_date)}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{e.supplier || "—"}</div>
                              <div className="text-xs text-muted-foreground">
                                {e.invoice_number || ""}
                              </div>
                            </td>
                            <td className="px-4 py-3">{categoryMap[e.category] || e.category}</td>
                            <td className="px-4 py-3">{e.cost_type === "fix" ? "Fix" : "Variabel"}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmtEur(e.amount || 0)}</td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEdit(e)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    if (window.confirm("Beleg wirklich löschen?")) deleteMutation.mutate(e.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </section>
        </TabsContent>
      </Tabs>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Beleg bearbeiten" : "Neuer Beleg"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="rounded-xl border p-4">
              <Label>PDF oder Foto</Label>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted">
                  <Upload className="h-4 w-4" />
                  Datei auswählen
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>

                <span className="text-xs text-muted-foreground break-all">
                  {file?.name || editing?.document?.filename || "Keine Datei ausgewählt"}
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                className="mt-3"
                disabled={aiBusy || (!file && !editing?.document)}
                onClick={analyze}
              >
                {aiBusy
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Sparkles className="h-4 w-4 mr-2" />}
                Mit AI analysieren
              </Button>

              <div className="text-xs text-muted-foreground mt-2">
                Die erkannten Angaben werden nur vorgeschlagen. Bitte vor dem Speichern prüfen.
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Lieferant</Label>
                <Input
                  className="mt-1"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                />
              </div>

              <div>
                <Label>Belegdatum</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={form.document_date}
                  onChange={(e) => setForm({ ...form, document_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Zahlungsdatum</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Wann hast du die Rechnung tatsächlich bezahlt?
                </div>
              </div>

              <div>
                <Label>Rechnungs-/Belegnummer</Label>
                <Input
                  className="mt-1"
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                />
              </div>

              {form.original_currency && form.original_currency !== "EUR" ? (
                <>
                  <div>
                    <Label>Originalbetrag</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={form.original_amount}
                        onChange={(e) => setForm({ ...form, original_amount: e.target.value })}
                      />
                      <Input
                        className="w-24"
                        value={form.original_currency}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            original_currency: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Betrag für Buchhaltung €</Label>
                    <Input
                      type="number"
                      step="0.01"
                      className="mt-1"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                    {form.exchange_rate_source && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Kurs: {form.exchange_rate_source}
                        {form.exchange_rate_date
                          ? ` · ${fmtDate(form.exchange_rate_date)}`
                          : ""}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <Label>Gesamtbetrag €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="mt-1"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
              )}

              <div>
                <Label>USt. €</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1"
                  value={form.vat_amount}
                  onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                  placeholder="optional"
                />
              </div>

              <div>
                <Label>Kategorie</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Kostenart</Label>
                <Select
                  value={form.cost_type}
                  onValueChange={(value) => setForm({ ...form, cost_type: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="variabel">Variabel</SelectItem>
                    <SelectItem value="fix">Fix</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Positionen / Produkte</Label>
              <Textarea
                className="mt-1 min-h-[90px]"
                value={(form.items || []).join("\n")}
                onChange={(e) => setForm({
                  ...form,
                  items: e.target.value.split("\n"),
                })}
                placeholder="Eine Position pro Zeile"
              />
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                className="mt-1"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.missing_info}
                  onChange={(e) => setForm({ ...form, missing_info: e.target.checked })}
                />
                Angabe fehlt
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.tax_review}
                  onChange={(e) => setForm({ ...form, tax_review: e.target.checked })}
                />
                Steuerlich klären
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Abbrechen
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !form.document_date}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
